/**
 * What a visitor is told where sign-up is closed (T-0615, D-0298, AC-W19c in
 * `docs/concept-ownership-organisations-and-instances.md`).
 *
 * Two deployments close sign-up for different reasons, and the visitor's next step differs:
 *
 * - **The public cloud before it opens** names its website (`FEATIT_WEBSITE_ORIGIN`), and the
 *   website takes early-access requests. A visitor there has no administrator to ask, so they are
 *   sent to the form.
 * - **A server somebody runs for themselves** names no website. Its visitor is told to ask whoever
 *   runs it.
 *
 * The sign-up page, the sign-in page, the OAuth refusal, the desktop's first screen and
 * `POST /auth/register`'s refusal all read the words from here, so a browser and a CLI caller are
 * never told two different things.
 */

/** Where the website takes early-access requests, as a path on its origin. */
export const EARLY_ACCESS_PAGE_PATH = '/early-access';

/**
 * The early-access form's address on a website origin, or `null` without a usable one: a server
 * that names no website has no form to send anybody to, and a link to a guessed host is worse than
 * none.
 */
export function earlyAccessUrl(websiteOrigin: string | null | undefined): string | null {
  const raw = websiteOrigin?.trim();
  if (!raw) return null;
  try {
    const url = new URL(raw);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return `${url.origin}${EARLY_ACCESS_PAGE_PATH}`;
  } catch {
    return null;
  }
}

export interface ISignUpClosedCopy {
  /** The sign-up page's heading. */
  readonly heading: string;
  /** What is going on, as one sentence. */
  readonly lead: string;
  /** What to do about it, as one sentence. */
  readonly next: string;
  /** `lead` and `next` together. */
  readonly body: string;
  /** The early-access form, when there is one to send the visitor to. */
  readonly earlyAccessUrl: string | null;
  /** The label of the link to it. */
  readonly actionLabel: string;
  /** The line under sign-in where *Create one* would otherwise be, before the link or the advice. */
  readonly signInNote: string;
  /** Under sign-in without an early-access form: who to ask instead. */
  readonly askInstead: string;
}

/** The screens' words, with and without an early-access form to point at. */
export function signUpClosedCopy(earlyAccess: string | null): ISignUpClosedCopy {
  const lead = earlyAccess
    ? "FEATit isn't open for sign-ups yet."
    : 'Sign-up is closed on this server.';
  const next = earlyAccess
    ? "Request early access and we'll let you know when your account is ready."
    : 'Ask whoever runs it to create an account for you.';
  return {
    heading: earlyAccess ? "Sign-ups aren't open yet" : 'Sign-up is closed',
    lead,
    next,
    body: `${lead} ${next}`,
    earlyAccessUrl: earlyAccess,
    actionLabel: earlyAccess ? 'Request early access' : '',
    signInNote: earlyAccess
      ? "New here? FEATit isn't open for sign-ups yet."
      : 'New here? Sign-up is closed.',
    askInstead: earlyAccess ? '' : 'Ask whoever runs this server for an account.',
  };
}

/**
 * The same answer as one sentence, for a caller that renders no link: `POST /auth/register`'s
 * refusal, which the CLI prints. With a form, its address is in the sentence.
 */
export function signUpClosedMessage(earlyAccess: string | null): string {
  return earlyAccess
    ? `FEATit isn't open for sign-ups yet. Request early access at ${earlyAccess} and we'll let you know when your account is ready.`
    : signUpClosedCopy(null).body;
}
